/**
 * Load / refetch the add-on catalogue (TMMPDM-0019). Server order, every status.
 * TMMPDM-0053: `refetch` resolves after the catalogue is stored, so Edit details can re-read
 * before it resets the form.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from '@/api/tmm/admin';
import { ApiError } from '@/api/errors';
import type { CatalogView } from '@/api/tmm/types';

export interface UseCatalog {
  items: CatalogView[];
  loading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
  refreshDetails: () => Promise<void>;
  acceptSaved: (item: CatalogView) => void;
}

export function useCatalog(): UseCatalog {
  const [items, setItems] = useState<CatalogView[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    const id = ++generation.current;
    let cancelled = false;
    adminApi
      .listAddonCatalog()
      .then((list) => {
        if (cancelled || generation.current !== id) return;
        setItems(list.addons);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled || generation.current !== id) return;
        setItems(null);
        setError(err instanceof ApiError ? err : new ApiError('UNKNOWN', 0));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refetch = useCallback(async () => {
    const id = ++generation.current;
    try {
      const list = await adminApi.listAddonCatalog();
      if (generation.current !== id) return;
      setItems(list.addons);
      setError(null);
    } catch (err: unknown) {
      if (generation.current !== id) return;
      setItems(null);
      setError(err instanceof ApiError ? err : new ApiError('UNKNOWN', 0));
    }
  }, []);

  return {
    items: items ?? [],
    loading: items === null && error === null,
    error,
    refetch,
    refreshDetails: async () => {
      const id = ++generation.current;
      const list = await adminApi.listAddonCatalog();
      if (generation.current !== id) return;
      setItems(list.addons);
      setError(null);
    },
    acceptSaved: (item) => {
      ++generation.current;
      setItems((current) => current?.map((row) => (row.kind === item.kind ? item : row)) ?? [item]);
    },
  };
}
